import { auth } from "../../lib/firebase";

const functionUrl = (name, configuredUrl = "") => {
  if (configuredUrl) return configuredUrl;
  const projectId = process.env.REACT_APP_FIREBASE_PROJECT_ID || "";
  return projectId ? `https://us-central1-${projectId}.cloudfunctions.net/${name}` : "";
};

const authenticatedRequest = async (name, configuredUrl, body = {}) => {
  const currentUser = auth.currentUser;
  if (!currentUser) throw new Error("Please verify your mobile number first.");
  const endpoint = functionUrl(name, configuredUrl);
  if (!endpoint) throw new Error("The passport service is not configured.");

  const idToken = await currentUser.getIdToken();
  const response = await fetch(endpoint, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${idToken}`,
    },
    body: JSON.stringify(body),
  });

  return { response, body: await response.json().catch(() => ({})) };
};

const responseError = (response, body, fallback) => {
  const error = new Error(body?.error || fallback);
  error.status = response.status;
  return error;
};

export const loadPublishedShows = async () => {
  const endpoint = functionUrl("listPublishedShows");
  if (!endpoint) return { shows: [] };
  const response = await fetch(endpoint);
  const body = await response.json().catch(() => ({}));
  if (!response.ok) throw responseError(response, body, "Published shows could not be loaded.");
  return body;
};

export const recordShowBookingIntent = (showId, method) => {
  const endpoint = functionUrl("recordShowBookingIntent");
  if (!endpoint || !showId) return Promise.resolve();
  return fetch(endpoint, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ showId, method }),
    keepalive: true,
  }).catch(() => {});
};

export const loadPublicEventQr = async (showId) => {
  const endpoint = functionUrl("getPublicEventQr");
  if (!endpoint) throw new Error("The event QR service is not configured.");
  const response = await fetch(`${endpoint}?showId=${encodeURIComponent(showId)}`);
  const body = await response.json().catch(() => ({}));
  if (!response.ok) throw responseError(response, body, "This event QR could not be loaded.");
  return body;
};

export const joinMyAudiencePassport = async () => {
  const { response, body } = await authenticatedRequest(
    "joinAudiencePassport",
    process.env.REACT_APP_JOIN_PASSPORT_URL
  );
  if (!response.ok) throw responseError(response, body, "We could not create your passport.");
  return body;
};

export const loadMyAudiencePassport = async () => {
  const { response, body } = await authenticatedRequest(
    "getAudiencePassport",
    process.env.REACT_APP_GET_PASSPORT_URL
  );
  if (!response.ok) throw responseError(response, body, "We could not load your passport.");
  return body;
};

export const claimVenueStamp = async (claimToken, attribution = {}) => {
  const { response, body } = await authenticatedRequest(
    "claimAudienceStamp",
    process.env.REACT_APP_CLAIM_STAMP_URL,
    { claimToken, attribution }
  );
  if (!response.ok) throw responseError(response, body, "We could not add this stamp.");
  return body;
};

export const downloadMyAudiencePassport = async () => {
  const currentUser = auth.currentUser;
  if (!currentUser) throw new Error("Please verify your mobile number first.");
  const endpoint = functionUrl(
    "downloadAudiencePassport",
    process.env.REACT_APP_DOWNLOAD_PASSPORT_URL
  );
  if (!endpoint) throw new Error("The passport download is not configured.");

  const idToken = await currentUser.getIdToken();
  const response = await fetch(endpoint, {
    method: "POST",
    headers: { Authorization: `Bearer ${idToken}` },
  });
  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new Error(body?.error || "We could not create your passport PDF.");
  }

  const disposition = response.headers.get("Content-Disposition") || "";
  const filenameMatch = disposition.match(/filename="?([^";]+)"?/i);
  return {
    blob: await response.blob(),
    filename: filenameMatch?.[1] || "valencia-comedy-passport.pdf",
  };
};

export const createMyGoogleWalletPass = async () => {
  const { response, body } = await authenticatedRequest(
    "createGoogleWalletPass",
    process.env.REACT_APP_GOOGLE_WALLET_URL
  );
  if (!response.ok) throw responseError(response, body, "We could not prepare your Google Wallet pass.");
  if (!body?.saveUrl) throw new Error("Google Wallet did not return a save link.");
  return body;
};

export const downloadMyAppleWalletPass = async () => {
  const currentUser = auth.currentUser;
  if (!currentUser) throw new Error("Please verify your mobile number first.");
  const endpoint = functionUrl(
    "downloadAppleWalletPass",
    process.env.REACT_APP_APPLE_WALLET_URL
  );
  if (!endpoint) throw new Error("The Apple Wallet service is not configured.");

  const idToken = await currentUser.getIdToken();
  const response = await fetch(endpoint, {
    method: "POST",
    headers: { "X-Firebase-Auth": `Bearer ${idToken}` },
  });
  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new Error(body?.error || "We could not prepare your Apple Wallet pass.");
  }
  return {
    blob: await response.blob(),
    filename: "valencia-comedy-passport.pkpass",
  };
};

export const verifyPublicPassport = async (token) => {
  const endpoint = functionUrl(
    "verifyAudiencePassport",
    process.env.REACT_APP_VERIFY_PASSPORT_URL
  );
  if (!endpoint) throw new Error("Passport verification is not configured.");
  let response;
  try {
    response = await fetch(`${endpoint}?token=${encodeURIComponent(token)}`);
  } catch (_error) {
    throw new Error("Passport verification is temporarily unavailable.");
  }
  const body = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(body?.error || "This passport could not be verified.");
  return body;
};

const organizerRequest = async (name, body) => {
  const endpoint = functionUrl(name);
  if (!endpoint) throw new Error("The organizer service is not configured.");
  const response = await fetch(endpoint, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) throw responseError(response, payload, "The organizer request could not be completed.");
  return payload;
};

export const openOrganizerAccessSession = (accessCode) => organizerRequest(
  "openOrganizerSession",
  { accessCode }
);

export const loadOrganizerDashboard = (sessionToken) => organizerRequest(
  "getOrganizerDashboard",
  { sessionToken }
);

export const saveOrganizerShowDraft = (sessionToken, show, imageDataUrl = "") => organizerRequest(
  "saveOrganizerShow",
  { sessionToken, show, imageDataUrl }
);

export const submitOrganizerShowDraft = (sessionToken, showId) => organizerRequest(
  "submitOrganizerShow",
  { sessionToken, showId }
);

export const reviewOrganizerShow = (adminCode, action = "list", showId = "", notes = "") => organizerRequest(
  "reviewOrganizerShow",
  { adminCode, action, showId, notes }
);

export const loadOrganizerAttendance = (sessionToken, showId) => organizerRequest(
  "organizerEventAttendance",
  { sessionToken, showId }
);

export const confirmOrganizerAttendance = (sessionToken, showId, passportToken) => organizerRequest(
  "organizerCheckInPassport",
  { sessionToken, showId, passportToken }
);

export const removeOrganizerAttendance = (sessionToken, showId, attendanceId, reason) => organizerRequest(
  "organizerRemoveAttendance",
  { sessionToken, showId, attendanceId, reason }
);

export const confirmAllOrganizerAttendance = (sessionToken, showId) => organizerRequest(
  "organizerConfirmAllAttendance",
  { sessionToken, showId }
);

export const undoLastOrganizerConfirmation = (sessionToken, showId) => organizerRequest(
  "organizerUndoLastConfirmation",
  { sessionToken, showId }
);

export const downloadOrganizerQrAsset = async (sessionToken, showId, format) => {
  const endpoint = functionUrl("getEventQrAsset");
  if (!endpoint) throw new Error("The QR download service is not configured.");
  const response = await fetch(endpoint, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ sessionToken, showId, format }),
  });
  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw responseError(response, body, "We could not create the QR document.");
  }
  return {
    blob: await response.blob(),
    filename: `${showId}-${format === "cards" ? "chair-qr-sheet" : "qr-poster"}.pdf`,
  };
};

export const passportTokenFromQrValue = (value) => {
  const trimmed = String(value || "").trim();
  if (!trimmed) return "";
  if (/^[A-Za-z0-9_-]{20,80}$/.test(trimmed)) return trimmed;
  try {
    const url = new URL(trimmed, window.location.origin);
    const match = url.pathname.match(/^\/passport\/verify\/([^/]+)\/?$/);
    return match ? decodeURIComponent(match[1]) : "";
  } catch (_error) {
    return "";
  }
};
